import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';
import { AnimateOnScroll } from './AnimateOnScroll';

const ServicesSection = () => {
  const navigate = useNavigate();
  const { t, tm } = useLanguage();
  const itemCopy = tm('home.services.items');

  const services = useMemo(
    () => [
      {
        id: 1,
        icon: 'Cpu',
        ...itemCopy[0],
        image: 'https://img.rocket.new/generatedImages/rocket_gen_img_18127f02f-1764714866990.png',
        color: 'text-amber-500',
        bgColor: 'bg-amber-500/10 border-amber-500/20',
        action: () => navigate('/service-portfolio-hub'),
      },
      {
        id: 2,
        icon: 'Users',
        ...itemCopy[1],
        image: 'https://images.unsplash.com/photo-1731374404945-d3fde8909690',
        color: 'text-blue-500',
        bgColor: 'bg-blue-500/10 border-blue-500/20',
        action: () => navigate('/job-marketplace'),
      },
      {
        id: 3,
        icon: 'Globe',
        ...itemCopy[2],
        image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1dd86d8a0-1764882792973.png',
        color: 'text-emerald-500',
        bgColor: 'bg-emerald-500/10 border-emerald-500/20',
        action: () => navigate('/global-trade-network'),
      },
      {
        id: 4,
        icon: 'Settings',
        ...itemCopy[3],
        image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1250b561c-1764657849234.png',
        color: 'text-indigo-500',
        bgColor: 'bg-indigo-500/10 border-indigo-500/20',
        action: () => navigate('/service-portfolio-hub'),
      },
    ],
    [itemCopy, navigate]
  );

  return (
    <section className="bg-white dark:bg-slate-950 py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <AnimateOnScroll>
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full mb-3">
              <Icon name="Sparkles" size={15} className="text-amber-500" />
              <span className="text-xs md:text-sm font-bold tracking-widest text-amber-600 dark:text-amber-400 uppercase">
                {t('home.services.badge')}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold text-foreground tracking-tight mb-3">
              {t('home.services.title')}
            </h2>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto font-normal">
              {t('home.services.subtitle')}
            </p>
          </div>
        </AnimateOnScroll>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
          {services.map((service, index) => (
            <AnimateOnScroll key={service.id} delay={index * 100}>
              <div className="bg-card rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1.5 border border-border/80 hover:border-amber-500/40 group flex flex-col h-full">
                <div className="relative h-48 md:h-56 overflow-hidden flex-shrink-0">
                  <Image
                    src={service.image}
                    alt={service.imageAlt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent" />
                  <div
                    className={`absolute top-4 left-4 w-12 h-12 md:w-14 md:h-14 rounded-xl ${service.bgColor} border backdrop-blur-md flex items-center justify-center`}
                  >
                    <Icon name={service.icon} size={24} className={service.color} />
                  </div>
                </div>

                <div className="p-6 md:p-8 flex flex-col justify-between flex-1">
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-sm md:text-base text-muted-foreground mb-6 leading-relaxed">
                      {service.description}
                    </p>

                    <div className="grid grid-cols-2 gap-2.5 mb-6">
                      {(service.features || []).map((feature, fIndex) => (
                        <div key={fIndex} className="flex items-center gap-2">
                          <Icon name="CheckCircle2" size={15} className={service.color} />
                          <span className="text-xs md:text-sm font-medium text-foreground">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button
                    variant="outline"
                    fullWidth
                    onClick={service.action}
                    iconName="ArrowRight"
                    iconPosition="right"
                    className="rounded-xl border-border/80 group-hover:bg-primary group-hover:text-white group-hover:border-primary transition-all font-bold"
                  >
                    {t('home.services.explore')}
                  </Button>
                </div>
              </div>
            </AnimateOnScroll>
          ))}
        </div>

        <AnimateOnScroll delay={400}>
          <div className="mt-12 text-center">
            <Button
              variant="default"
              size="lg"
              onClick={() => navigate('/service-portfolio-hub')}
              iconName="Grid"
              iconPosition="left"
              className="px-8 py-3.5 rounded-xl bg-primary hover:bg-primary/90 text-white font-extrabold shadow-md"
            >
              {t('home.services.viewAll')}
            </Button>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};

export default ServicesSection;
